/**
 * Observability layer for VelloPlay
 * Tracks critical errors across auth, Supabase queries, payments, and arena/tournament ops
 */

import { logger } from './logger';

type ErrorDomain = 'auth' | 'query' | 'rpc' | 'edge-function' | 'payment' | 'arena' | 'tournament' | 'bracket' | 'unknown';

interface ObservabilityEvent {
  domain: ErrorDomain;
  action: string;
  error: unknown;
  meta?: Record<string, unknown>;
}

const recentErrors: ObservabilityEvent[] = [];
const MAX_ERRORS = 50;

function classifyPostgrestCode(code: string | undefined): string {
  if (!code) return 'unknown';
  if (code === '42501') return 'RLS denied';
  if (code === '23505') return 'duplicate';
  if (code === '23503') return 'FK violation';
  if (code === 'PGRST301') return 'JWT expired';
  if (code.startsWith('PGRST')) return `PostgREST:${code}`;
  return code;
}

export function trackError(event: ObservabilityEvent): void {
  const err = event.error as any;
  const detail = {
    domain: event.domain,
    action: event.action,
    code: classifyPostgrestCode(err?.code),
    message: err?.message || String(err),
    status: err?.status,
    ...event.meta,
  };

  logger.error(`[obs] ${event.domain}/${event.action}`, err instanceof Error ? err : undefined, {
    component: 'Observability',
    ...detail,
  });

  recentErrors.push(event);
  if (recentErrors.length > MAX_ERRORS) recentErrors.shift();
}

/** Detect domain from supabase query key or generic context */
export function detectDomain(queryKey: readonly unknown[]): ErrorDomain {
  const key = queryKey.join('/').toLowerCase();
  if (key.includes('auth') || key.includes('session') || key.includes('profile')) return 'auth';
  if (key.includes('payment') || key.includes('checkout') || key.includes('mercadopago') || key.includes('fatura')) return 'payment';
  if (key.includes('arena') || key.includes('court') || key.includes('quadra') || key.includes('turma') || key.includes('aluno')) return 'arena';
  if (key.includes('tournament') || key.includes('torneio') || key.includes('bracket') || key.includes('match')) return 'tournament';
  return 'query';
}

/** Global React Query error handler */
export function handleQueryError(error: unknown, query: { queryKey: readonly unknown[] }): void {
  trackError({
    domain: detectDomain(query.queryKey),
    action: `query:${query.queryKey[0]}`,
    error,
    meta: { queryKey: query.queryKey },
  });
}

/** Global React Query mutation error handler */
export function handleMutationError(error: unknown, _variables: unknown, _context: unknown, mutation: any): void {
  const key = mutation?.options?.mutationKey || ['unknown-mutation'];
  trackError({
    domain: detectDomain(key),
    action: `mutation:${key[0]}`,
    error,
    meta: { mutationKey: key },
  });
}

/** Get recent errors for debug panel */
export function getRecentErrors(): readonly ObservabilityEvent[] {
  return recentErrors;
}
